import React, { useState } from 'react';
import { 
  CreditCard, 
  Clock, 
  CheckCircle, 
  XCircle, 
  AlertTriangle,
  User,
  Shield,
  Crown,
  TrendingUp,
  Calendar,
  DollarSign,
  Building2,
  Smartphone,
  Bitcoin
} from 'lucide-react';
import { useAccount } from '../contexts/AccountContext';
import { useAuth } from '../contexts/AuthContext';

const AccountDetails: React.FC = () => {
  const { accountBalance, transactions, accountDetails } = useAccount();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<'payments' | 'transactions' | 'profile' | 'kyc' | 'userType'>('payments');

  const tabs = [
    { id: 'payments', name: 'Payments', icon: CreditCard },
    { id: 'transactions', name: 'Transactions', icon: Clock },
    { id: 'profile', name: 'Profile', icon: User },
    { id: 'kyc', name: 'KYC Verification', icon: Shield },
    { id: 'userType', name: 'User Type', icon: Crown },
  ];

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-[#2AB7A6]" />;
      case 'pending':
        return <Clock className="h-4 w-4 text-yellow-400" />;
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-400" />;
      case 'cancelled':
        return <XCircle className="h-4 w-4 text-gray-400" />;
      default:
        return <AlertTriangle className="h-4 w-4 text-yellow-400" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'text-[#2AB7A6]';
      case 'pending':
        return 'text-yellow-400';
      case 'failed':
        return 'text-red-400';
      case 'cancelled':
        return 'text-gray-400';
      default:
        return 'text-yellow-400';
    }
  };

  const getTransactionIcon = (type: string) => {
    switch (type) {
      case 'deposit':
        return <DollarSign className="h-4 w-4 text-[#2AB7A6]" />;
      case 'withdrawal':
        return <DollarSign className="h-4 w-4 text-red-400" />;
      case 'trade_pnl':
        return <TrendingUp className="h-4 w-4 text-blue-400" />;
      case 'fee':
        return <CreditCard className="h-4 w-4 text-yellow-400" />;
      default:
        return <Clock className="h-4 w-4 text-gray-400" />;
    }
  };

  const formatTransactionType = (type: string) => {
    switch (type) {
      case 'deposit':
        return 'Deposit';
      case 'withdrawal':
        return 'Withdrawal';
      case 'trade_pnl':
        return 'Trade PnL';
      case 'fee':
        return 'Fee';
      default:
        return type.charAt(0).toUpperCase() + type.slice(1);
    }
  };

  const getPaymentMethodIcon = (method?: string) => {
    if (!method) return <CreditCard className="h-4 w-4" />;
    
    if (method.toLowerCase().includes('bank')) return <Building2 className="h-4 w-4" />;
    if (method.toLowerCase().includes('upi')) return <Smartphone className="h-4 w-4" />;
    if (method.toLowerCase().includes('crypto')) return <Bitcoin className="h-4 w-4" />;
    
    return <CreditCard className="h-4 w-4" />;
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const getRelativeTime = (date: Date) => {
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInHours / 24);

    if (diffInDays > 0) {
      return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
    } else if (diffInHours > 0) {
      return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
    } else {
      return 'Just now';
    }
  };

  return (
    <div className="min-h-screen theme-bg-primary theme-text-primary">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold theme-text-primary">Account Details</h1>
          <p className="theme-text-quaternary mt-2">Manage your account settings, view transactions, and monitor your trading activity</p>
        </div>

        {/* Account Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="theme-bg-secondary rounded-lg p-6 theme-border border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm theme-text-quaternary">Demo Balance</p>
                <p className="text-2xl font-bold theme-text-primary">${accountBalance.demoBalance.toLocaleString()}</p>
              </div>
              <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center">
                <TrendingUp className="h-6 w-6 text-white" />
              </div>
            </div>
          </div>

          <div className="theme-bg-secondary rounded-lg p-6 theme-border border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm theme-text-quaternary">Live Balance</p>
                <p className="text-2xl font-bold theme-text-primary">${accountBalance.liveBalance.toLocaleString()}</p>
              </div>
              <div className="w-12 h-12 bg-[#2AB7A6] rounded-lg flex items-center justify-center">
                <DollarSign className="h-6 w-6 text-white" />
              </div>
            </div>
          </div>

          <div className="theme-bg-secondary rounded-lg p-6 theme-border border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm theme-text-quaternary">Equity</p>
                <p className="text-2xl font-bold theme-text-primary">${accountBalance.equity.toLocaleString()}</p>
              </div>
              <div className="w-12 h-12 bg-purple-600 rounded-lg flex items-center justify-center">
                <CreditCard className="h-6 w-6 text-white" />
              </div>
            </div>
          </div>

          <div className="theme-bg-secondary rounded-lg p-6 theme-border border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm theme-text-quaternary">Margin Level</p>
                <p className="text-2xl font-bold theme-text-primary">{accountBalance.marginLevel}%</p>
              </div>
              <div className="w-12 h-12 bg-yellow-600 rounded-lg flex items-center justify-center">
                <Shield className="h-6 w-6 text-white" />
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="theme-bg-secondary rounded-lg theme-border border">
          <div className="border-b theme-border">
            <nav className="flex space-x-8 px-6" aria-label="Tabs">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id as any)}
                    className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 transition-colors duration-200 ${
                      activeTab === tab.id
                        ? 'border-[#2AB7A6] text-[#2AB7A6]'
                        : 'border-transparent theme-text-quaternary hover:theme-text-secondary hover:theme-border-secondary'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    <span>{tab.name}</span>
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {/* Payments Tab */}
            {activeTab === 'payments' && (
              <div>
                <h3 className="text-lg font-medium theme-text-primary mb-4">Payment Methods</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {[
                    { name: 'Net Banking', icon: Building2, status: 'active', color: 'bg-blue-600' },
                    { name: 'UPI', icon: Smartphone, status: 'active', color: 'bg-purple-600' },
                    { name: 'Skrill', icon: CreditCard, status: 'active', color: 'bg-orange-600' },
                    { name: 'Stripe', icon: CreditCard, status: 'active', color: 'bg-indigo-600' },
                    { name: 'PayPal', icon: CreditCard, status: 'active', color: 'bg-blue-500' },
                    { name: 'Cryptocurrency', icon: Bitcoin, status: 'active', color: 'bg-yellow-600' },
                  ].map((method, index) => {
                    const Icon = method.icon;
                    return (
                      <div key={index} className="theme-bg-quaternary rounded-lg p-4 theme-border-secondary border">
                        <div className="flex items-center space-x-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${method.color}`}>
                            <Icon className="h-5 w-5 text-white" />
                          </div>
                          <div className="flex-1">
                            <div className="theme-text-primary font-medium">{method.name}</div>
                            <div className="flex items-center space-x-2">
                              <div className="w-2 h-2 bg-[#2AB7A6] rounded-full"></div>
                              <span className="text-sm text-[#2AB7A6]">Active</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Transactions Tab */}
            {activeTab === 'transactions' && (
              <div>
                <h3 className="text-lg font-medium theme-text-primary mb-4">Transaction History</h3>
                <div className="space-y-3">
                  {transactions.map((transaction) => (
                    <div key={transaction.id} className="theme-bg-quaternary rounded-lg p-4 theme-border-secondary border">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          {getTransactionIcon(transaction.type)}
                          <div>
                            <div className="theme-text-primary font-medium">{formatTransactionType(transaction.type)}</div>
                            <div className="text-sm theme-text-quaternary">
                              {transaction.method && (
                                <span className="flex items-center space-x-1">
                                  {getPaymentMethodIcon(transaction.method)}
                                  <span>{transaction.method}</span>
                                </span>
                              )}
                            </div>
                            <div className="text-xs theme-text-quaternary">
                              {formatDate(transaction.timestamp)} • {getRelativeTime(transaction.timestamp)}
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className={`text-lg font-bold ${
                            transaction.type === 'deposit' ? 'text-[#2AB7A6]' : 
                            transaction.type === 'withdrawal' ? 'text-red-400' : 
                            transaction.type === 'trade_pnl' ? 'text-blue-400' : 'theme-text-quaternary'
                          }`}>
                            {transaction.type === 'deposit' ? '+' : ''}${transaction.amount.toLocaleString()}
                          </div>
                          <div className="text-sm theme-text-quaternary">Balance: ${transaction.balance.toLocaleString()}</div>
                          <div className="flex items-center space-x-2 mt-1">
                            {getStatusIcon(transaction.status)}
                            <span className={`text-sm ${getStatusColor(transaction.status)}`}>
                              {transaction.status.charAt(0).toUpperCase() + transaction.status.slice(1)}
                            </span>
                          </div>
                        </div>
                      </div>
                      {transaction.reference && (
                        <div className="mt-3 pt-3 border-t theme-border-secondary">
                          <span className="text-xs theme-text-quaternary">Reference: {transaction.reference}</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Profile Tab */}
            {activeTab === 'profile' && (
              <div>
                <h3 className="text-lg font-medium theme-text-primary mb-4">Profile Information</h3>
                <div className="theme-bg-quaternary rounded-lg p-6 theme-border-secondary border">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium theme-text-quaternary mb-2">Full Name</label>
                      <div className="theme-text-primary">{user?.displayName || 'N/A'}</div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium theme-text-quaternary mb-2">Email</label>
                      <div className="theme-text-primary">{user?.email || 'N/A'}</div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium theme-text-quaternary mb-2">Account ID</label>
                      <div className="theme-text-primary font-mono">{accountDetails?.accountId || 'N/A'}</div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium theme-text-quaternary mb-2">Account Type</label>
                      <div className="theme-text-primary capitalize">{accountDetails?.accountType || 'N/A'}</div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium theme-text-quaternary mb-2">Status</label>
                      <div className="flex items-center space-x-2">
                        <div className={`w-2 h-2 rounded-full ${
                          accountDetails?.status === 'active' ? 'bg-[#2AB7A6]' : 
                          accountDetails?.status === 'suspended' ? 'bg-red-500' : 'bg-yellow-500'
                        }`}></div>
                        <span className="theme-text-primary capitalize">{accountDetails?.status || 'N/A'}</span>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium theme-text-quaternary mb-2">Created</label>
                      <div className="theme-text-primary">
                        {accountDetails?.createdAt ? formatDate(accountDetails.createdAt) : 'N/A'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* KYC Tab */}
            {activeTab === 'kyc' && (
              <div>
                <h3 className="text-lg font-medium theme-text-primary mb-4">KYC Verification Status</h3>
                <div className="theme-bg-quaternary rounded-lg p-6 theme-border-secondary border">
                  <div className="flex items-center space-x-4 mb-6">
                    <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
                      accountDetails?.kycStatus === 'verified' ? 'bg-[#2AB7A6]' : 
                      accountDetails?.kycStatus === 'rejected' ? 'bg-red-600' : 'bg-yellow-600'
                    }`}>
                      {accountDetails?.kycStatus === 'verified' ? (
                        <CheckCircle className="h-8 w-8 text-white" />
                      ) : accountDetails?.kycStatus === 'rejected' ? (
                        <XCircle className="h-8 w-8 text-white" />
                      ) : (
                        <Clock className="h-8 w-8 text-white" />
                      )}
                    </div>
                    <div>
                      <div className="text-xl font-medium theme-text-primary">
                        {accountDetails?.kycStatus === 'verified' ? 'KYC Verified' : 
                         accountDetails?.kycStatus === 'rejected' ? 'KYC Rejected' : 'KYC Pending'}
                      </div>
                      <div className="theme-text-quaternary">
                        {accountDetails?.kycStatus === 'verified' ? 'Your identity has been verified successfully' : 
                         accountDetails?.kycStatus === 'rejected' ? 'Your KYC documents were not approved' : 'Your KYC verification is in progress'}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 theme-bg-primary rounded-lg">
                      <div className="flex items-center space-x-3">
                        <CheckCircle className="h-5 w-5 text-[#2AB7A6]" />
                        <span className="theme-text-primary">Identity Verification</span>
                      </div>
                      <span className="text-[#2AB7A6] text-sm">Completed</span>
                    </div>
                    <div className="flex items-center justify-between p-4 theme-bg-primary rounded-lg">
                      <div className="flex items-center space-x-3">
                        <CheckCircle className="h-5 w-5 text-[#2AB7A6]" />
                        <span className="theme-text-primary">Address Verification</span>
                      </div>
                      <span className="text-[#2AB7A6] text-sm">Completed</span>
                    </div>
                    <div className="flex items-center justify-between p-4 theme-bg-primary rounded-lg">
                      <div className="flex items-center space-x-3">
                        <CheckCircle className="h-5 w-5 text-[#2AB7A6]" />
                        <span className="theme-text-primary">Document Verification</span>
                      </div>
                      <span className="text-[#2AB7A6] text-sm">Completed</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* User Type Tab */}
            {activeTab === 'userType' && (
              <div>
                <h3 className="text-lg font-medium theme-text-primary mb-4">User Type & Benefits</h3>
                <div className="theme-bg-quaternary rounded-lg p-6 theme-border-secondary border">
                  <div className="flex items-center space-x-4 mb-6">
                    <div className="w-16 h-16 bg-yellow-600 rounded-full flex items-center justify-center">
                      <Crown className="h-8 w-8 text-white" />
                    </div>
                    <div>
                      <div className="text-xl font-medium theme-text-primary capitalize">
                        {accountDetails?.userType || 'Standard'} User
                      </div>
                      <div className="theme-text-quaternary">
                        {accountDetails?.userType === 'premium' ? 'Premium trading features and priority support' :
                         accountDetails?.userType === 'vip' ? 'Exclusive VIP benefits and dedicated account manager' :
                         'Standard trading account with basic features'}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="theme-bg-primary rounded-lg p-4 theme-border border">
                      <div className="text-center">
                        <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center mx-auto mb-3">
                          <TrendingUp className="h-6 w-6 text-white" />
                        </div>
                        <div className="theme-text-primary font-medium mb-2">Trading Features</div>
                        <div className="text-sm theme-text-quaternary">
                          {accountDetails?.userType === 'premium' ? 'Advanced charts, indicators, and analysis tools' :
                           accountDetails?.userType === 'vip' ? 'Premium indicators, custom strategies, and AI insights' :
                           'Basic charts and standard indicators'}
                        </div>
                      </div>
                    </div>

                    <div className="theme-bg-primary rounded-lg p-4 theme-border border">
                      <div className="text-center">
                        <div className="w-12 h-12 bg-[#2AB7A6] rounded-lg flex items-center justify-center mx-auto mb-3">
                          <Shield className="h-6 w-6 text-white" />
                        </div>
                        <div className="theme-text-primary font-medium mb-2">Support Level</div>
                        <div className="text-sm theme-text-quaternary">
                          {accountDetails?.userType === 'premium' ? 'Priority email and chat support' :
                           accountDetails?.userType === 'vip' ? '24/7 dedicated account manager' :
                           'Standard email support'}
                        </div>
                      </div>
                    </div>

                    <div className="theme-bg-primary rounded-lg p-4 theme-border border">
                      <div className="text-center">
                        <div className="w-12 h-12 bg-purple-600 rounded-lg flex items-center justify-center mx-auto mb-3">
                          <DollarSign className="h-6 w-6 text-white" />
                        </div>
                        <div className="theme-text-primary font-medium mb-2">Fees & Limits</div>
                        <div className="text-sm theme-text-quaternary">
                          {accountDetails?.userType === 'premium' ? 'Reduced trading fees and higher limits' :
                           accountDetails?.userType === 'vip' ? 'Minimal fees and unlimited trading' :
                           'Standard fees and limits'}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountDetails;